import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { platformTypes, type PlatformType } from '@backend/lib/db/schema/platform'
import type { WorkspaceCaptions, WorkspaceTitles } from '@backend/lib/db/schema/workspace'
import type { WorkspaceMedia } from '@backend/lib/db/typeRoots/workspaceMedia'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { Button } from '@/components/ui/shadcn/button'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { DownloadIconNew } from '@/components/ui/icons/icons-new/download-icon-new'
import { getPlatformDisplayName } from '@/lib/calendar/types'
import { buildAndDownloadExport } from '@/lib/exportWorkspace'
import { captureError } from '@/lib/errors'
import { cn } from '@/lib/utils'
import { trpc } from '@/config/trpc'
import type { AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'

interface Props {
  workspaceMedia: WorkspaceMedia[]
  captions: WorkspaceCaptions
  platformTitles?: WorkspaceTitles
  workspaceTitle: string
  /** Only captions for these (enabled) platforms are exported. */
  activePlatforms: PlatformType[]
  disabled?: boolean
  /** Whether this post publishes with a visible AI label baked in. When true the
   *  export has to carry it too, or the exported file is not the file that
   *  ships. */
  bakeAiLabel?: boolean
  aiLabelStyle?: AiLabelStyle
  /** IPTC claim to restate after the bake re-encodes. */
  aiProvenance?: 'generated' | 'composite' | null
}

const EXTENSION_BY_TYPE: Record<WorkspaceMedia['type'], string> = {
  image: 'png',
  video: 'mp4',
  document: 'pdf',
  canvas: 'png',
}

/** Original filename if it already carries an extension, else a synthesized one. */
function mediaFilename(media: WorkspaceMedia, index: number): string {
  const original = media.file.file.name
  if (original && /\.[a-z0-9]+$/i.test(original)) return original
  return `${media.type}-${index + 1}.${EXTENSION_BY_TYPE[media.type]}`
}

function zipNameFromTitle(title: string): string {
  const base = title
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return `${base || 'post-export'}.zip`
}

export function WorkspaceExportDialog({
  workspaceMedia,
  captions,
  platformTitles,
  workspaceTitle,
  activePlatforms,
  disabled,
  bakeAiLabel = false,
  aiLabelStyle,
  aiProvenance,
}: Props) {
  const [open, setOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const { mutateAsync: bakeLabel } = trpc.asset.bakeAiLabel.useMutation()
  // Indices of media items selected for export (defaults to all).
  const [selectedMedia, setSelectedMedia] = useState<Set<number>>(new Set())
  const [includeCaptions, setIncludeCaptions] = useState(true)

  // Enabled platforms that actually have caption text to export.
  const captionedPlatforms = useMemo<PlatformType[]>(
    () =>
      platformTypes.filter(
        (p) => activePlatforms.includes(p) && (captions[p]?.trim() ?? '') !== '',
      ),
    [captions, activePlatforms],
  )

  const exportableMedia = useMemo(
    () => workspaceMedia.filter((m) => Boolean(m.file.url)),
    [workspaceMedia],
  )

  // Reset selections each time the dialog opens so it reflects current content.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setSelectedMedia(new Set(exportableMedia.map((_, i) => i)))
      setIncludeCaptions(captionedPlatforms.length > 0)
    }
    setOpen(next)
  }

  const toggleMedia = (index: number) => {
    setSelectedMedia((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const selectedMediaCount = selectedMedia.size
  const willIncludeCaptions = includeCaptions && captionedPlatforms.length > 0
  const nothingSelected = selectedMediaCount === 0 && !willIncludeCaptions

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const chosen = exportableMedia
        .map((m, i) => ({ m, i }))
        .filter(({ i }) => selectedMedia.has(i))

      // Bake the label server-side, through the same path the publish flow uses,
      // so the exported file matches what platforms receive and keeps its IPTC
      // provenance marker. Only images can carry one.
      //
      // A failure here degrades to the unlabelled original rather than failing
      // the export: losing the label on a local download is bad, losing the
      // user's export is worse. `labelled` tells us which happened.
      const resolved = await Promise.all(
        chosen.map(async ({ m, i }) => {
          const filename = mediaFilename(m, i)
          const isImage = m.type === 'image' || m.type === 'canvas'
          if (!bakeAiLabel || !isImage) return { url: m.file.url, filename, labelled: true }
          try {
            const { url, labelled } = await bakeLabel({
              url: m.file.url,
              style: aiLabelStyle,
              // Without this the bake's re-encode strips the IPTC marker, which
              // is the whole reason this goes through the server rather than a
              // canvas.
              provenance: aiProvenance ?? undefined,
            })
            return { url, filename, labelled }
          } catch {
            return { url: m.file.url, filename, labelled: false }
          }
        }),
      )

      if (resolved.some((r) => !r.labelled)) {
        toast.warning('Some images were exported without the AI label.')
      }

      await buildAndDownloadExport({
        media: resolved.map(({ url, filename }) => ({ url, filename })),
        captions: willIncludeCaptions
          ? captionedPlatforms.map((p) => ({
              platform: p,
              title: platformTitles?.[p]?.trim() || undefined,
              text: captions[p] ?? '',
            }))
          : [],
        zipName: zipNameFromTitle(workspaceTitle),
      })
      setOpen(false)
    } catch (error) {
      captureError(error, { scope: 'workspace-export' })
      toast.error('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button
        variant="outline"
        onClick={() => handleOpenChange(true)}
        disabled={disabled}
        className="rounded-lg">
        <DownloadIconNew color="var(--foreground)" />
        Export
      </Button>

      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Export post</DialogTitle>
          <DialogDescription>
            Download the selected media and captions as a single zip file.
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          {/* Media selection */}
          <div className="flex flex-col gap-2">
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              Media ({selectedMediaCount}/{exportableMedia.length})
            </p>
            {exportableMedia.length === 0 ? (
              <p className="text-muted-foreground text-sm">No media in this post.</p>
            ) : (
              exportableMedia.map((media, index) => {
                const isImage = media.type === 'image' || media.type === 'canvas'
                const checked = selectedMedia.has(index)
                return (
                  <label
                    key={`${media.file.url}-${index}`}
                    className={cn(
                      'border-border hover:bg-hover flex cursor-pointer items-center gap-3 rounded-lg border p-2',
                    )}>
                    <Checkbox checked={checked} onCheckedChange={() => toggleMedia(index)} />
                    {isImage ? (
                      <img
                        src={media.file.url}
                        alt=""
                        className="bg-muted h-10 w-10 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <div className="bg-muted text-foreground grid h-10 w-10 shrink-0 place-content-center rounded text-[10px] font-semibold uppercase">
                        {media.type === 'video' ? 'MP4' : 'PDF'}
                      </div>
                    )}
                    <span className="truncate text-sm">{mediaFilename(media, index)}</span>
                  </label>
                )
              })
            )}
          </div>

          {/* Caption toggle */}
          <div className="flex flex-col gap-2">
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              Text
            </p>
            {captionedPlatforms.length === 0 ? (
              <p className="text-muted-foreground text-sm">No captions to export.</p>
            ) : (
              <label className="border-border hover:bg-hover flex cursor-pointer items-center gap-3 rounded-lg border p-2">
                <Checkbox
                  checked={includeCaptions}
                  onCheckedChange={(v) => setIncludeCaptions(v === true)}
                />
                <span className="text-sm">
                  Include captions
                  <span className="text-muted-foreground">
                    {' '}
                    ({captionedPlatforms.map(getPlatformDisplayName).join(', ')})
                  </span>
                </span>
              </label>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={isExporting || nothingSelected}>
            <DownloadIconNew color="white" />
            {isExporting ? 'Exporting…' : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
